import { useState, FormEvent, useCallback, useEffect, useRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import {
  UserPlus,
  Building2,
  Users,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  ChevronLeft,
  Tag,
  ShoppingCart,
  Minus,
  Plus,
  Loader2,
} from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import logoEspaceClientTexte from '../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../images/Logo-Espace-client-texte-dark.png'
import PhoneInput from '../components/PhoneInput'
import { useSeo } from '../hooks/useSeo'

// Captcha helper
function generateCaptcha(): { a: number; b: number; answer: number } {
  const a = Math.floor(Math.random() * 10) + 1
  const b = Math.floor(Math.random() * 10) + 1
  return { a, b, answer: a + b }
}

// Registered ERP/supplier for autocomplete
interface PublicERP {
  id: number
  name: string
  siren: string | null
}

// ─── Plan summary banner shown at the top of the supplier form ───────────────
interface SelectedPlan {
  id: number
  name: string
  base_price: number
  description: string
  is_custom: boolean
  max_users?: number
  max_clients?: number
  max_signatures?: number
  max_ai_tokens?: number
  max_emails?: number
}

function PlanBanner({ plan }: { plan: SelectedPlan }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-purple-50 border border-purple-200">
      <Tag className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
      <p className="text-xs text-purple-800 truncate">
        <span className="font-semibold">{plan.name}</span>
        {plan.is_custom ? (
          <span className="ml-1 text-purple-600">— Sur devis</span>
        ) : (
          plan.base_price > 0 && (
            <span className="ml-1 text-purple-600">
              — {plan.base_price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
              /mois
            </span>
          )
        )}
        {plan.description && <span className="ml-1 text-purple-400">· {plan.description}</span>}
      </p>
    </div>
  )
}

// ─── Quota display helper ──────────────────────────────────────────────────
const QUOTA_ROWS = [
  { key: 'max_users', label: 'Utilisateurs' },
  { key: 'max_clients', label: 'Clients' },
  { key: 'max_signatures', label: 'Signatures/mois' },
  { key: 'max_ai_tokens', label: 'Tokens IA/mois' },
  { key: 'max_emails', label: 'Emails/mois' },
]
function fmtQ(n: number) {
  return n === 0
    ? '∞'
    : n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(1)}M`
      : n >= 1_000
        ? `${(n / 1_000).toFixed(0)}k`
        : String(n)
}

// ─── Cart step ─────────────────────────────────────────────────────────────
interface CartStepProps {
  plan: SelectedPlan & {
    max_users?: number
    max_clients?: number
    max_signatures?: number
    max_ai_tokens?: number
    max_emails?: number
  }
  quantity: number
  setQuantity: (q: number) => void
  onOrder: () => void
  onBack: () => void
  loading: boolean
  error: string | null
}
function CartStep({ plan, quantity, setQuantity, onOrder, onBack, loading, error }: CartStepProps) {
  const total = plan.base_price * quantity
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <ShoppingCart className="w-4 h-4 text-primary-600" /> Votre sélection
      </div>

      {/* Plan summary card */}
      <div className="rounded-xl border border-primary-200 bg-primary-50 p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="font-bold text-primary-800">{plan.name}</p>
            <p className="text-xs text-primary-500">{plan.description}</p>
          </div>
          <p className="text-lg font-bold text-primary-700 whitespace-nowrap">
            {plan.base_price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            <span className="text-xs font-normal">/pack/mois</span>
          </p>
        </div>

        {/* Quotas per pack */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-primary-700">
          {QUOTA_ROWS.map((r) => {
            const val = plan[r.key as keyof typeof plan] as number | undefined
            if (val === undefined) return null
            return (
              <div key={r.key} className="flex justify-between gap-1">
                <span className="text-primary-500">{r.label}</span>
                <strong>{fmtQ(val * quantity)}</strong>
              </div>
            )
          })}
        </div>
      </div>

      {/* Quantity selector */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre de packs</label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors disabled:opacity-40"
            disabled={quantity <= 1}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
          <button
            type="button"
            onClick={() => setQuantity(quantity + 1)}
            className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs text-gray-500 ml-1">
            ={' '}
            <strong className="text-gray-800">
              {total.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}/mois
            </strong>
            <span className="ml-1 text-purple-600">
              · {(total * 1000).toLocaleString('fr-FR')} crédits/mois
            </span>
          </span>
        </div>
      </div>

      {error && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <button
          type="button"
          onClick={onOrder}
          disabled={loading}
          className="flex-1 py-2 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ShoppingCart className="w-4 h-4" />
          )}
          Procéder au paiement
        </button>
      </div>
    </div>
  )
}

// ─── Stripe inline payment form ────────────────────────────────────────────
interface InlinePaymentFormProps {
  amountEur: number
  planName: string
  onSuccess: (piId: string) => void
  onBack: () => void
}
function InlinePaymentFormInner({
  amountEur,
  planName,
  onSuccess,
  onBack,
}: InlinePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return
    setLoading(true)
    setError(null)
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    })
    if (stripeError) {
      setError(stripeError.message ?? 'Erreur de paiement')
      setLoading(false)
    } else if (paymentIntent) {
      onSuccess(paymentIntent.id)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-xl border border-purple-200 bg-purple-50 px-4 py-3">
        <p className="text-xs text-purple-600">
          Plan <strong>{planName}</strong>
        </p>
        <p className="text-xl font-bold text-purple-800">
          {amountEur.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
          <span className="text-xs font-normal text-purple-500"> / mois</span>
        </p>
      </div>
      <PaymentElement />
      {error && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
          {error}
        </div>
      )}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-medium rounded-lg shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Payer {amountEur.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
        </button>
      </div>
    </form>
  )
}

// ─── Supplier registration form ────────────────────────────────────────────
function SupplierForm({
  onBack,
  initialComment = '',
  planId,
  initialQuantity = 1,
}: {
  onBack: () => void
  initialComment?: string
  planId?: number
  initialQuantity?: number
}) {
  const { t } = useTranslation()
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null)

  // Multi-step flow when planId is set
  type FormStep = 'form' | 'cart' | 'payment' | 'success'
  const [formStep, setFormStep] = useState<FormStep>('form')
  const [requestId, setRequestId] = useState(0)
  const [quantity, setQuantity] = useState(Math.max(1, initialQuantity))
  const [clientSecret, setClientSecret] = useState('')
  const [publishableKey, setPublishableKey] = useState('')
  const [amountEur, setAmountEur] = useState(0)
  const [cartError, setCartError] = useState<string | null>(null)
  const [cartLoading, setCartLoading] = useState(false)

  // Lazy-load Stripe only when payment step is reached
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  )
  const [form, setForm] = useState({
    company_name: '',
    siren: '',
    lastname: '',
    firstname: '',
    email: '',
    phone: '',
    erp_type: '',
    comment: initialComment,
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState(generateCaptcha)
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaError, setCaptchaError] = useState(false)

  // Load plan details when a plan_id is provided via URL
  useEffect(() => {
    if (!planId) return
    api
      .getPublicPricingPlans()
      .then((plans) => {
        const found = plans.find((p) => p.id === planId)
        if (found) setSelectedPlan(found)
      })
      .catch(() => {})
  }, [planId])

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha())
    setCaptchaInput('')
    setCaptchaError(false)
  }, [])

  const update = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setCaptchaError(false)
    if (parseInt(captchaInput, 10) !== captcha.answer) {
      setCaptchaError(true)
      refreshCaptcha()
      return
    }
    setLoading(true)
    try {
      if (planId && selectedPlan?.is_custom) {
        // Custom plan (Entreprise): save registration, no cart or payment
        await api.savePublicRegistration({ ...form, plan_id: planId })
        setFormStep('success')
      } else if (planId) {
        // Standard plan: save to DB for abandonment tracking, then show cart
        const res = await api.savePublicRegistration({ ...form, plan_id: planId })
        setRequestId(res.request_id)
        setFormStep('cart')
      } else {
        // Standard flow: send registration request directly
        const result = await api.registerRequest({ ...form, request_type: 'supplier' })
        setSuccess(result.message)
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr.response?.data?.error || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  // Called from CartStep: create PaymentIntent and move to payment
  const handleOrder = async () => {
    if (!selectedPlan) return
    setCartLoading(true)
    setCartError(null)
    try {
      const res = await api.initiatePublicSubscription({
        plan_id: selectedPlan.id,
        quantity,
        request_id: requestId,
        ...form,
      })
      setClientSecret(res.client_secret)
      setPublishableKey(res.publishable_key)
      setAmountEur(res.amount_eur)
      setFormStep('payment')
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setCartError(apiErr?.response?.data?.error ?? 'Erreur lors de la création du paiement.')
    } finally {
      setCartLoading(false)
    }
  }

  // Called when Stripe confirms payment
  const handlePaymentSuccess = async (piId: string) => {
    try {
      await api.confirmPublicSubscription(requestId, piId)
    } catch {
      /* best-effort */
    }
    setFormStep('success')
  }

  // ── Success screen ─────────────────────────────────────────────────────────
  if (success || formStep === 'success') {
    return (
      <div className="space-y-3 text-center py-2">
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
        </div>
        {success ? (
          <p className="text-sm text-green-800">{success}</p>
        ) : (
          <>
            <p className="font-semibold text-gray-800">Paiement confirmé !</p>
            <p className="text-sm text-gray-500">
              Votre demande est enregistrée. Notre équipe vous contactera sous 24–48 h pour
              finaliser la configuration de votre espace client.
            </p>
          </>
        )}
      </div>
    )
  }

  // ── Cart step ──────────────────────────────────────────────────────────────
  if (formStep === 'cart') {
    return (
      <CartStep
        plan={selectedPlan!}
        quantity={quantity}
        setQuantity={setQuantity}
        onOrder={handleOrder}
        onBack={() => setFormStep('form')}
        loading={cartLoading}
        error={cartError}
      />
    )
  }

  // ── Payment step ───────────────────────────────────────────────────────────
  if (formStep === 'payment' && clientSecret && stripePromise) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
        <InlinePaymentFormInner
          amountEur={amountEur}
          planName={selectedPlan?.name ?? ''}
          onSuccess={handlePaymentSuccess}
          onBack={() => setFormStep('cart')}
        />
      </Elements>
    )
  }

  // ── Registration form ──────────────────────────────────────────────────────
  const fieldCls =
    'w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none'
  const labelCls = 'block text-xs font-medium text-gray-600 mb-0.5'

  return (
    <form onSubmit={handleSubmit} className="space-y-2.5">
      {selectedPlan && <PlanBanner plan={selectedPlan} />}

      {error && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      {/* Row 1: company name (full width) */}
      <div>
        <label className={labelCls}>{t('register.companyName')}</label>
        <input
          type="text"
          value={form.company_name}
          onChange={(e) => update('company_name', e.target.value)}
          required
          className={fieldCls}
        />
      </div>

      {/* Row 2: SIREN + ERP type */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div>
          <label className={labelCls}>{t('register.siren')}</label>
          <input
            type="text"
            value={form.siren}
            onChange={(e) => update('siren', e.target.value.replace(/\D/g, '').slice(0, 9))}
            required
            maxLength={9}
            placeholder={t('register.sirenPlaceholder')}
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t('register.erpType')}</label>
          <select
            value={form.erp_type}
            onChange={(e) => update('erp_type', e.target.value)}
            required
            className={fieldCls}
          >
            <option value="">{t('register.selectErp')}</option>
            <option value="cegid">Cegid XRP Flex</option>
            <option value="divalto">Divalto Infinity</option>
            <option value="dolibarr">Dolibarr</option>
            <option value="erpnext">ERPNext</option>
            <option value="business_central">Microsoft Business Central</option>
            <option value="netsuite">NetSuite</option>
            <option value="odoo">Odoo</option>
            <option value="sage100">Sage 100</option>
            <option value="salesforce">Salesforce</option>
            <option value="zoho">Zoho One</option>
            <option value="autre">Autre</option>
          </select>
        </div>
      </div>

      {/* Row 3: last name + first name */}
      <div className="grid grid-cols-2 gap-2.5">
        <div>
          <label className={labelCls}>{t('register.lastname')}</label>
          <input
            type="text"
            value={form.lastname}
            onChange={(e) => update('lastname', e.target.value)}
            required
            className={fieldCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t('register.firstname')}</label>
          <input
            type="text"
            value={form.firstname}
            onChange={(e) => update('firstname', e.target.value)}
            required
            className={fieldCls}
          />
        </div>
      </div>

      {/* Row 4: email + phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <div>
          <label className={labelCls}>{t('register.email')}</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            required
            className={fieldCls}
          />
        </div>
        <div>
          <PhoneInput
            value={form.phone}
            onChange={(v) => update('phone', v)}
            required
            fieldCls={fieldCls}
            labelCls={labelCls}
            label={t('register.phone')}
            placeholder={t('register.phonePlaceholder')}
          />
        </div>
      </div>

      {/* Comment */}
      <div>
        <label className={labelCls}>
          {form.erp_type === 'autre' ? t('register.erpBrandLabel') : t('register.commentLabel')}
        </label>
        {form.erp_type === 'autre' && (
          <p className="text-xs text-amber-600 mb-0.5">{t('register.erpBrandHint')}</p>
        )}
        <textarea
          value={form.comment}
          onChange={(e) => update('comment', e.target.value)}
          rows={2}
          required={form.erp_type === 'autre'}
          placeholder={form.erp_type === 'autre' ? t('register.erpBrandPlaceholder') : ''}
          className={`${fieldCls} resize-none`}
        />
      </div>

      {/* Captcha — inline compact */}
      <div>
        <label className={labelCls}>{t('register.captchaLabel')}</label>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${captchaError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
        >
          <span className="font-mono font-semibold text-gray-800 select-none">
            {t('register.captchaQuestion', { a: captcha.a, b: captcha.b })}
          </span>
          <span className="text-gray-400">=</span>
          <input
            type="number"
            value={captchaInput}
            onChange={(e) => {
              setCaptchaInput(e.target.value)
              setCaptchaError(false)
            }}
            required
            min="0"
            max="20"
            placeholder="?"
            className={`w-16 px-2 py-0.5 rounded border text-center outline-none focus:ring-2 focus:ring-primary-500 text-sm ${captchaError ? 'border-red-400' : 'border-gray-300'}`}
          />
          <button
            type="button"
            onClick={refreshCaptcha}
            title="Nouveau calcul"
            className="ml-auto p-0.5 text-gray-400 hover:text-primary-600 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {captchaError && (
          <p className="mt-0.5 text-xs text-red-600">{t('register.captchaError')}</p>
        )}
      </div>

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-2 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : planId && !selectedPlan?.is_custom ? (
            <ShoppingCart className="w-4 h-4" />
          ) : (
            <UserPlus className="w-4 h-4" />
          )}
          {loading
            ? t('register.loading')
            : planId && selectedPlan?.is_custom
              ? 'Envoyer ma demande'
              : planId
                ? 'Valider et voir le panier'
                : t('register.submit')}
        </button>
      </div>
    </form>
  )
}

// ─── Client registration form ───────────────────────────────────────────────
function ClientForm({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()

  // Supplier autocomplete
  const [erps, setErps] = useState<PublicERP[]>([])
  const [supplierQuery, setSupplierQuery] = useState('')
  const [supplierDropdown, setSupplierDropdown] = useState(false)
  const [selectedErp, setSelectedErp] = useState<PublicERP | null>(null)
  const supplierRef = useRef<HTMLDivElement>(null)

  const [form, setForm] = useState({
    company_name: '',
    siren: '', // client SIREN (optional)
    lastname: '',
    firstname: '',
    email: '',
    phone: '',
    supplier_name: '',
    supplier_siren: '',
    comment: '',
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState(generateCaptcha)
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaError, setCaptchaError] = useState(false)

  // Load ERP list for autocomplete
  useEffect(() => {
    api
      .getPublicERPs()
      .then(setErps)
      .catch(() => {})
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (supplierRef.current && !supplierRef.current.contains(e.target as Node)) {
        setSupplierDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredErps = erps.filter(
    (e) =>
      e.name.toLowerCase().includes(supplierQuery.toLowerCase()) ||
      (e.siren && e.siren.includes(supplierQuery))
  )

  const selectErp = (erp: PublicERP) => {
    setSelectedErp(erp)
    setSupplierQuery(erp.name)
    setForm((f) => ({ ...f, supplier_name: erp.name, supplier_siren: erp.siren ?? '' }))
    setSupplierDropdown(false)
  }

  const handleSupplierInput = (value: string) => {
    setSupplierQuery(value)
    setSelectedErp(null)
    setForm((f) => ({ ...f, supplier_name: value, supplier_siren: '' }))
    setSupplierDropdown(true)
  }

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha())
    setCaptchaInput('')
    setCaptchaError(false)
  }, [])

  const update = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setCaptchaError(false)
    if (parseInt(captchaInput, 10) !== captcha.answer) {
      setCaptchaError(true)
      refreshCaptcha()
      return
    }
    if (!form.supplier_name.trim()) {
      setError(t('register.client.supplierRequired'))
      return
    }
    setLoading(true)
    try {
      const payload = {
        request_type: 'client',
        ...form,
        supplier_erp_id: selectedErp?.id ?? null,
      }
      const result = await api.registerRequest(payload)
      setSuccess(result.message)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr?.response?.data?.error || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-green-800">{success}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Supplier section */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs font-semibold text-blue-900 uppercase mb-3">
          {t('register.client.supplierSection')}
        </p>

        {/* Supplier name with autocomplete */}
        <div className="mb-3" ref={supplierRef}>
          <label className="block text-sm font-medium text-black mb-1">
            {t('register.client.supplierName')}
          </label>
          <div className="relative">
            <input
              type="text"
              value={supplierQuery}
              onChange={(e) => handleSupplierInput(e.target.value)}
              onFocus={() => supplierQuery.length > 0 && setSupplierDropdown(true)}
              required
              placeholder={t('register.client.supplierNamePlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
            {selectedErp && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                {t('register.client.knownSupplier')}
              </span>
            )}
            {supplierDropdown && supplierQuery.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredErps.length > 0 ? (
                  filteredErps.map((erp) => (
                    <button
                      key={erp.id}
                      type="button"
                      onClick={() => selectErp(erp)}
                      className="w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{erp.name}</span>
                      {erp.siren && (
                        <span className="ml-2 text-xs text-gray-500">SIREN: {erp.siren}</span>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500 italic">
                    {t('register.client.supplierNotFound')}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Supplier SIREN */}
        <div>
          <label className="block text-sm font-medium text-black mb-1">
            {t('register.client.supplierSiren')}
          </label>
          <input
            type="text"
            value={form.supplier_siren}
            onChange={(e) =>
              update('supplier_siren', e.target.value.replace(/\D/g, '').slice(0, 9))
            }
            required={!selectedErp}
            maxLength={9}
            placeholder={t('register.sirenPlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Client section */}
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-3">
          {t('register.client.clientSection')}
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.companyName')}
            </label>
            <input
              type="text"
              value={form.company_name}
              onChange={(e) => update('company_name', e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.client.clientSiren')}
            </label>
            <input
              type="text"
              value={form.siren}
              onChange={(e) => update('siren', e.target.value.replace(/\D/g, '').slice(0, 9))}
              maxLength={9}
              placeholder={t('register.sirenPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.lastname')}
              </label>
              <input
                type="text"
                value={form.lastname}
                onChange={(e) => update('lastname', e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('register.firstname')}
              </label>
              <input
                type="text"
                value={form.firstname}
                onChange={(e) => update('firstname', e.target.value)}
                required
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.email')}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              required
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <PhoneInput
              value={form.phone}
              onChange={(v) => update('phone', v)}
              fieldCls="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              labelCls="block text-sm font-medium text-gray-700 mb-1"
              label={t('register.phone')}
              placeholder={t('register.phonePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.commentLabel')}
            </label>
            <textarea
              value={form.comment}
              onChange={(e) => update('comment', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none"
            />
          </div>
        </div>
      </div>

      {/* Captcha */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('register.captchaLabel')}
        </label>
        <div
          className={`flex items-center gap-3 p-3 rounded-lg border ${captchaError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
        >
          <span className="font-mono text-base font-semibold text-gray-800 select-none">
            {t('register.captchaQuestion', { a: captcha.a, b: captcha.b })}
          </span>
          <input
            type="number"
            value={captchaInput}
            onChange={(e) => {
              setCaptchaInput(e.target.value)
              setCaptchaError(false)
            }}
            required
            min="0"
            max="20"
            placeholder={t('register.captchaPlaceholder')}
            className={`w-24 px-2 py-1 rounded border text-center outline-none focus:ring-2 focus:ring-primary-500 ${captchaError ? 'border-red-400' : 'border-gray-300'}`}
          />
          <button
            type="button"
            onClick={refreshCaptcha}
            title="Nouveau calcul"
            className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {captchaError && <p className="mt-1 text-xs text-red-600">{t('register.captchaError')}</p>}
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <UserPlus className="w-5 h-5" />
          {loading ? t('register.loading') : t('register.submit')}
        </button>
      </div>
    </form>
  )
}

// ─── Main Register page ─────────────────────────────────────────────────────
export default function Register() {
  const { t } = useTranslation()
  useSeo({ title: t('seo.registerTitle') })
  const [searchParams] = useSearchParams()

  // Auto-select step and pre-fill comment from URL query params
  // e.g. /register?type=supplier&comment=Intérêt+offre+Entreprise
  const initialType = searchParams.get('type')
  const initialComment = searchParams.get('comment') ?? ''
  const initialPlanId = searchParams.get('plan_id')
    ? parseInt(searchParams.get('plan_id')!, 10)
    : undefined
  const initialQuantity = searchParams.get('quantity')
    ? Math.max(1, parseInt(searchParams.get('quantity')!, 10))
    : 1

  const [step, setStep] = useState<'choose' | 'supplier' | 'client'>(
    initialType === 'supplier' ? 'supplier' : initialType === 'client' ? 'client' : 'choose'
  )

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMS4xLS45LTItMi0yaC00Yy0xLjEgMC0yIC45LTIgMnY0YzAgMS4xLjkgMiAyIDJoNGMxLjEgMCAyLS45IDItMnYtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-500 via-accent-500 to-primary-600"></div>

        <div className="p-5 sm:p-7">
          <div className="text-center mb-4">
            <Link
              to="/"
              aria-label={t('common.backToHome')}
              title={t('common.backToHome')}
              className="group inline-flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 mb-1.5"
            >
              <img
                src={logoEspaceClientTexte}
                alt="Espace Client"
                className="h-10 w-auto transition-transform group-hover:scale-105 dark:hidden"
              />
              <img
                src={logoEspaceClientTexteDark}
                alt=""
                aria-hidden="true"
                className="hidden h-10 w-auto transition-transform group-hover:scale-105 dark:block"
              />
            </Link>
            <p className="text-sm text-gray-600">{t('register.subtitle')}</p>
          </div>

          {/* Step: choose type */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center mb-4">
                {t('register.chooseTypeLabel')}
              </p>

              <button
                onClick={() => setStep('supplier')}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-all group"
              >
                <div className="w-12 h-12 bg-primary-100 group-hover:bg-primary-200 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 group-hover:text-gray-900">
                    {t('register.typeSupplier')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-900">
                    {t('register.typeSupplierDesc')}
                  </p>
                </div>
              </button>

              <button
                onClick={() => setStep('client')}
                className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-accent-400 hover:bg-accent-50 transition-all group"
              >
                <div className="w-12 h-12 bg-accent-100 group-hover:bg-accent-200 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                  <Users className="w-6 h-6 text-accent-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900 group-hover:text-gray-900">
                    {t('register.typeClient')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-900">
                    {t('register.typeClientDesc')}
                  </p>
                </div>
              </button>
            </div>
          )}

          {step === 'supplier' && (
            <SupplierForm
              onBack={() => setStep('choose')}
              initialComment={initialComment}
              planId={initialPlanId}
              initialQuantity={initialQuantity}
            />
          )}
          {step === 'client' && <ClientForm onBack={() => setStep('choose')} />}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('register.alreadyAccount')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
